import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api/apiClient';
import {
  CreateOpportunityResponse,
  InvestorListOpportunityInterestsResponse,
  PsiListOpportunityInterestsResponse,
  CreateOpportunityInterestBody,
  PsiService,
} from '@inversor-hub/types';
import { AxiosResponse } from 'axios';
import { LIST_OPPORTUNITIES_KEY } from '@/lib/api/opportunities.ts';

export const LIST_INVESTORS_OPPORTUNITY_INTERESTS = 'investor-opportunity-interests';
const LIST_PSI_OPPORTUNITY_INTERESTS = 'psi-opportunity-interests';

export function useCreateOpportunityInterest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      opportunityId,
      selectedServices,
    }: {
      opportunityId: string;
      selectedServices: PsiService[];
    }) => {
      const createOpportunityResponse = await apiClient.post<
        unknown,
        AxiosResponse<CreateOpportunityResponse>,
        CreateOpportunityInterestBody
      >(`/opportunities/${opportunityId}/interests`, { selectedServices });
      return createOpportunityResponse.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTORS_OPPORTUNITY_INTERESTS] });
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useListInvestorOpportunityInterests() {
  return useQuery({
    queryKey: [LIST_INVESTORS_OPPORTUNITY_INTERESTS],
    queryFn: async () => {
      return apiClient.get<InvestorListOpportunityInterestsResponse>(`/opportunity-interests`);
    },
    select: (response) => response.data.data,
  });
}

export function useListPsiOpportunityInterests(opportunityId?: string) {
  return useQuery({
    queryKey: [LIST_PSI_OPPORTUNITY_INTERESTS, opportunityId],
    queryFn: async () => {
      return apiClient.get<PsiListOpportunityInterestsResponse>(
        `/opportunities/${opportunityId}/interests`
      );
    },
    enabled: !!opportunityId,
    select: (response) => response.data.data,
  });
}

export function useDeleteOpportunityInterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/opportunities/${id}/interests`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_INVESTORS_OPPORTUNITY_INTERESTS] });
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}

export function useSelectOpportunityInterest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/opportunity-interests/${id}/select`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LIST_PSI_OPPORTUNITY_INTERESTS] });
      queryClient.invalidateQueries({ queryKey: [LIST_OPPORTUNITIES_KEY] });
    },
  });
}
